import { type SeverityLevel } from '@sentry/react';

import {
	GUARANTEED,
	MAYBE,
	NONE,
	type ProbabilityOfBug,
} from '../../../common/utils/probability-of-bug';
import { type TelemetryScopeContext } from '../events/sentry-browser/bridge';
import { type CommerceService, UNKNOWN } from '../service';
import { responsibleServiceIncludesCommerceLibraries } from '../service/utils';

export type TelemetryTags = {
	responsibleService: CommerceService;
	probabilityOfBug: ProbabilityOfBug;
};

export type SetTelemetryExceptionSentryTagsInput = {
	responsibleService?: CommerceService;
	probabilityOfBug: ProbabilityOfBug;
};

/**
 * Keep in mind what we're really using Sentry's "severity" not as a metric for severity but
 * for colour coding (more red = More you should go take a look at the log)
 */
export const calculateSeverityFromTags = ({
	probabilityOfBug,
	responsibleService,
}: TelemetryTags): SeverityLevel => {
	switch (probabilityOfBug) {
		case GUARANTEED:
			if (responsibleServiceIncludesCommerceLibraries(responsibleService)) {
				return 'fatal';
			}

			return 'error';

		case MAYBE:
			return 'warning';
		case NONE:
			return 'log';
		default:
			// Should never happen so sending critical :3
			return 'fatal';
	}
};

const severityOrdering = new Map<SeverityLevel | undefined, number>([
	['fatal', 0],
	['fatal', 1],
	['error', 2],
	['warning', 3],
	['info', 4],
	['log', 5],
	[undefined, 6],
]);

export const pickWorstSeverity = (
	severity1: SeverityLevel,
	severity2: SeverityLevel,
): SeverityLevel => {
	if (severityOrdering.get(severity1)! <= severityOrdering.get(severity2)!) {
		return severity1;
	}

	return severity2;
};

export const withTelemetryInfoScopeContext = <
	T extends Partial<Omit<TelemetryScopeContext, 'tags'>> & {
		tags: SetTelemetryExceptionSentryTagsInput;
	},
>(
	scopeContext: T,
): T & Pick<TelemetryScopeContext, 'tags' | 'level'> => {
	const tags = {
		...scopeContext.tags,
		responsibleService: scopeContext.tags.responsibleService ?? UNKNOWN,
	};

	return {
		...scopeContext,
		tags,
		level: calculateSeverityFromTags(tags),
	};
};
